import React, { useMemo, useCallback } from "react";
import {
    ScrollablePane,
    ScrollbarVisibility,
    Selection,
    IContextualMenuItem,
    IconButton,
    DirectionalHint,
    ConstrainMode,
    CheckboxVisibility
} from "office-ui-fabric-react";
import { EntityType, UpdateOptions, updateCheckboxOptions } from "../../../entities/common";
import * as Metadata from "../../../entities/Metadata";
import * as TimeReportingPeriodsStore from "../../../store/TimeReportingPeriodsStore";
import EntityDetailsList, { TOnInlineEditComplete, onRenderDetailsHeader } from "../../common/extensibleEntity/EntityDetailsList";
import { ClearFilterComponent } from "../../common/sectionsControl/SectionPlaceholder";
import { namesof } from "../../../store/services/metadataService";
import { notUndefined } from "../../utils/common";

interface Props {
    fields: Metadata.Field[];
    allPeriods: TimeReportingPeriodsStore.TimeReportingPeriod[];
    filteredPeriods: TimeReportingPeriodsStore.TimeReportingPeriod[];
    selection: Selection;
    onUpdate: (id: string, update: TimeReportingPeriodsStore.BulkUpdatePeriodsModel, options?: UpdateOptions) => Promise<void>;
    onDelete: (id: string) => void;
    onClearFilter: () => void;
    timesheetApprovalsEnabled: boolean;
    canManage: boolean;
}

const getPeriodKey = (period: TimeReportingPeriodsStore.TimeReportingPeriod): string => period.id;

const TimeReportingPeriodsTable = (props: Props) => {
    const {
        fields,
        allPeriods,
        filteredPeriods,
        selection,
        onUpdate,
        onDelete,
        onClearFilter,
        timesheetApprovalsEnabled,
        canManage
    } = props;

    const getItemCommands = useCallback(
        (item: TimeReportingPeriodsStore.TimeReportingPeriod): IContextualMenuItem[] => {
            return [
                {
                    key: "set-status",
                    name: "Set Status",
                    iconProps: { iconName: "CircleRing" },
                    subMenuProps: {
                        items: [
                            {
                                key: "status-open",
                                name: "Open",
                                onClick: () => {
                                    onUpdate(item.id, { status: TimeReportingPeriodsStore.TimeReportingPeriodStatus.Open })
                                }
                            },
                            {
                                key: "status-closed",
                                name: "Closed",
                                onClick: () => {
                                    onUpdate(item.id, { status: TimeReportingPeriodsStore.TimeReportingPeriodStatus.Closed })
                                }
                            }
                        ]
                    }
                },
                timesheetApprovalsEnabled
                    ? {
                          key: "set-approval-required",
                          name: "Set Approval Required",
                          iconProps: { iconName: "DocumentApproval" },
                          subMenuProps: {
                              items: [
                                  {
                                      key: "approval-yes",
                                      name: "Yes",
                                      onClick: () => { onUpdate(item.id, { approvalRequired: true }) }
                                  },
                                  {
                                      key: "approval-no",
                                      name: "No",
                                      onClick: () => { onUpdate(item.id, { approvalRequired: false }) }
                                  }
                              ]
                          }
                      }
                    : undefined,
                {
                    key: "delete",
                    name: "Delete",
                    iconProps: { iconName: "Delete" },
                    onClick: () => onDelete(item.id)
                }
            ].filter(notUndefined);
        },
        [onUpdate, onDelete, timesheetApprovalsEnabled]
    );

    const renderThreeDotsMenu = useCallback(
        (period: TimeReportingPeriodsStore.TimeReportingPeriod) => {
            const items = getItemCommands(period);
            return (
                <div className="menu">
                    <IconButton
                        menuIconProps={{ iconName: "PPMXMore" }}
                        menuProps={{
                            directionalHint: DirectionalHint.bottomRightEdge,
                            items
                        }}
                    />
                </div>
            );
        },
        [getItemCommands]
    );

    const readonlyFields = useMemo(
        () => namesof<TimeReportingPeriodsStore.TimeReportingPeriodAttrs>(["Name", "StartDate", "EndDate"]),
        []
    );

    const handleInlineEditComplete: TOnInlineEditComplete<TimeReportingPeriodsStore.TimeReportingPeriod> = useCallback(
        (field, item, value) => {
            if (field.name === "Status") {
                return onUpdate(item.id, { status: value }, { inlineEdit: true });
            } else if (field.name === "ApprovalRequired") {
                return onUpdate(item.id, { approvalRequired: value }, { inlineEdit: true });
            }
            return Promise.resolve();
        },
        [onUpdate]
    );

    const displayFields = useMemo(() => {
        return namesof<TimeReportingPeriodsStore.TimeReportingPeriodAttrs>([
            "Name",
            "StartDate",
            "EndDate",
            "Status",
            ...(timesheetApprovalsEnabled ? ["ApprovalRequired"] : [])
        ]);
    }, [timesheetApprovalsEnabled]);

    return (
        <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
            <ClearFilterComponent items={allPeriods} filteredItems={filteredPeriods} onClearFilter={onClearFilter}>
                <EntityDetailsList
                    getKey={getPeriodKey}
                    entities={filteredPeriods}
                    entityType={EntityType.TimeReportingPeriod}
                    fields={fields}
                    displayFields={displayFields}
                    selection={selection}
                    onItemMenuRender={canManage ? renderThreeDotsMenu : undefined}
                    inlineEditProps={canManage ? {
                        onInlineEditComplete: handleInlineEditComplete,
                        readonlyFields: readonlyFields,
                    } : undefined}
                    {...updateCheckboxOptions(
                        {
                            checkboxVisibility: !canManage ? CheckboxVisibility.hidden : undefined
                        },
                        canManage ? [] : undefined,
                        true
                    )}
                    fabricListProps={{
                        constrainMode: ConstrainMode.unconstrained,
                        onRenderDetailsHeader: onRenderDetailsHeader
                    }}
                />
            </ClearFilterComponent>
        </ScrollablePane>
    );
};

export default TimeReportingPeriodsTable;
